/**
 * Cart drawer sync after an add.
 *
 * Shopify can render a cart section from a basket that is a few seconds old. This covers the sections bundled into a
 * cart/add.js response and a follow-up ?sections= request, which the browser can also answer from a revalidated
 * cached copy. On this store the lag was measured at 0.3 to 2.5 seconds (2026-09-11). A drawer rendered that way shows
 * the previous basket: it can list a line that was just removed and miss the line just added. Removing that stale line
 * then fails, because its line key no longer exists.
 *
 * cart.js is always current. afterAdd() only returns a drawer render that agrees with cart.js, and the buy buttons hand
 * it to the theme through cart:change, as Impact's own product form does. It returns null when there is no cart drawer,
 * or when no agreeing render arrives before the 5,000 ms display deadline; the caller then opens the basket page. It
 * never changes the basket.
 */
(() => {
  if (window.RepelloCartDrawerSync) {
    return;
  }

  const TOTAL_DEADLINE_MS = 5000;
  // Waits between render checks. They share the total deadline rather than adding another budget on top.
  const RETRY_DELAYS_MS = [250, 500, 750, 1000, 1500];

  /**
   * Whether a drawer render shows the basket. `rendered.lines` maps each line key the render can edit to its quantity;
   * `rendered.itemCount` is the item count the render shows, or null if it shows none.
   */
  const renderMatchesCart = (rendered, cart) => {
    const cartQuantities = new Map(cart.items.map((item) => [item.key, item.quantity]));

    for (const [key, quantity] of rendered.lines) {
      if (cartQuantities.get(key) !== quantity) {
        return false;
      }
    }

    if (rendered.itemCount !== null) {
      return rendered.itemCount === cart.item_count;
    }

    return rendered.lines.size === cartQuantities.size;
  };

  // Reads the quantity inputs and item count that snippets/line-item.liquid and sections/cart-drawer.liquid render.
  const readRender = (html) => {
    const drawer = new DOMParser().parseFromString(html, 'text/html');
    const lines = new Map();

    for (const input of drawer.querySelectorAll('input[data-line-key]')) {
      lines.set(input.getAttribute('data-line-key'), Number(input.getAttribute('value')));
    }

    const itemCount = Number.parseInt(drawer.querySelector('cart-count')?.textContent ?? '', 10);

    return { lines, itemCount: Number.isInteger(itemCount) ? itemCount : null };
  };

  const drawerRoot = (html) => new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');

  // Impact commits a nonempty drawer from inner + footer, and an empty drawer from the empty-state root.
  const isUsableDrawerHtml = (html, cart) => {
    if (typeof html !== 'string' || html === '') {
      return false;
    }

    const root = drawerRoot(html);

    if (!root) {
      return false;
    }

    if (cart.item_count === 0) {
      return Boolean(root.querySelector('.empty-state'));
    }

    return Boolean(root.querySelector('.cart-drawer__inner') && root.querySelector('[slot="footer"]'));
  };

  const drawerSectionId = () => {
    const section = document.querySelector('cart-drawer')?.closest('.shopify-section');

    return section ? section.id.replace('shopify-section-', '') : '';
  };

  const abortError = (signal) => signal?.reason instanceof Error
    ? signal.reason
    : new DOMException('The display sync was aborted.', 'AbortError');

  const wait = (milliseconds, signal) => new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError(signal));
      return;
    }

    const id = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, milliseconds);

    const onAbort = () => {
      window.clearTimeout(id);
      reject(abortError(signal));
    };

    signal.addEventListener('abort', onAbort, { once: true });
  });

  const readJson = async (response, signal) => {
    const body = Promise.resolve(response.json());
    body.catch(() => {});

    if (signal.aborted) {
      throw abortError(signal);
    }

    return await new Promise((resolve, reject) => {
      const onAbort = () => {
        signal.removeEventListener('abort', onAbort);
        reject(abortError(signal));
      };

      signal.addEventListener('abort', onAbort, { once: true });
      body.then(
        (value) => {
          signal.removeEventListener('abort', onAbort);
          resolve(value);
        },
        (error) => {
          signal.removeEventListener('abort', onAbort);
          reject(error);
        }
      );
    });
  };

  const fetchCart = async (signal) => {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, { cache: 'no-store', signal });

    return response.ok ? readJson(response, signal) : null;
  };

  // no-store skips the browser's cached copy and the unique query skips any shared cache.
  const fetchDrawer = async (sectionId, signal) => {
    const url = `${window.Shopify.routes.root}?sections=${encodeURIComponent(sectionId)}&repello_cart_sync=${Date.now()}`;
    const response = await fetch(url, { cache: 'no-store', signal });

    if (!response.ok) {
      return null;
    }

    const payload = await readJson(response, signal);
    return payload?.[sectionId] ?? null;
  };

  /**
   * Resolves to cart.js plus a `sections` map whose drawer render agrees with it, in the shape Impact's cart:change
   * listeners read, or to null. `bundledSections` are the sections returned with the cart/add.js response, if any.
   * `options.signal` cancels only this display work; it is not the add-request timeout.
   */
  const afterAdd = async (bundledSections = null, options = {}) => {
    const sectionId = drawerSectionId();

    if (!sectionId || options.signal?.aborted) {
      return null;
    }

    const displayController = new AbortController();
    const { signal } = displayController;
    const timeoutId = window.setTimeout(() => displayController.abort(), TOTAL_DEADLINE_MS);

    const onCallerAbort = () => displayController.abort();
    options.signal?.addEventListener('abort', onCallerAbort, { once: true });

    const agreeing = (cart, html) => (cart && html && isUsableDrawerHtml(html, cart) && renderMatchesCart(readRender(html), cart)
      ? { ...cart, sections: { ...bundledSections, [sectionId]: html } }
      : null);

    try {
      let synced = agreeing(await fetchCart(signal), bundledSections?.[sectionId] ?? null);

      for (const delay of RETRY_DELAYS_MS) {
        if (synced || signal.aborted) {
          break;
        }

        await wait(delay, signal);

        if (signal.aborted) {
          break;
        }

        const [cart, html] = await Promise.all([fetchCart(signal), fetchDrawer(sectionId, signal)]);
        synced = agreeing(cart, html);
      }

      return signal.aborted ? null : synced;
    } catch {
      return null;
    } finally {
      window.clearTimeout(timeoutId);
      options.signal?.removeEventListener('abort', onCallerAbort);
      displayController.abort();
    }
  };

  window.RepelloCartDrawerSync = { afterAdd, core: { renderMatchesCart } };
})();
